import { ButtonPrimary } from "@nova/component-btn"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAsyncCallback } from "@nova/use-async-callback"
import {
    AnyAction,
    GenericUpload,
    ID,
    ImageProcessingState,
    isUsable,
    OrthoProcessingMode,
    SidePanelLayer,
} from "@nova/constants"
import EventEmitter from "events"
import {
    activateFlightPath,
    addUploadTimestamp,
    deactivateFlightPath,
} from "features/map/layers/slice"
import { UploadState, useUploadSpeedForFlightId } from "@nova/slice-multifile-upload"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { useAddOnRestricted } from "features/data-management/workspace/subscriptions/hooks/useAddOnRestricted"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { AutoProcessToggle } from "panels/content/side-panel/upload-flow/components/AutoProcessToggle"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { GenericDataUsage } from "panels/content/side-panel/upload-flow/components/GenericDataUsage"
import { useRemoveSelectedUploads } from "panels/content/side-panel/upload-flow/preview-files/useRemoveSelectedUploads"
import { PreviewFilesViewSwitcher } from "panels/content/side-panel/upload-flow/preview-files/PreviewFilesViewSwitcher"
import { useImportArrivalWatcher } from "panels/content/side-panel/upload-flow/LoadFromLayer"
import { PreviewFilesUploads } from "panels/content/side-panel/upload-flow/preview-files/PreviewFilesUploads"
import { PreviewFilesWarningsAndErrors } from "panels/content/side-panel/upload-flow/preview-files/PreviewFilesWarningsAndErrors"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { UploadWarningModal } from "panels/content/side-panel/upload-flow/preview-files/UploadWarningModal"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { RadioButton } from "@nova/ds-component-radio-button"
import classNames from "classnames"
import { flightExceededUsage } from "panels/content/side-panel/upload-flow/module"
import { ProcessingOptionsModal } from "features/modals/ProcessingOptionsModal"
import { RapidModeModal, RAPID_MODE_DONT_SHOW_KEY } from "features/modals/RapidModeModal"
import {
    selectCsvFlights,
    selectUploadsForSet as selectRawOrthoUploadsForSet,
} from "./OrthomosaicSlice"
import { useCsvTelemetryGating } from "features/job-management/uploader/mma-telemetry"

export const MAX_UPLOADS_FOR_URBAN_MODE = 2000

export const AutoProcessController = new EventEmitter()
const processingModeCache = {} as Record<string, OrthoProcessingMode>

export const OrthomosaicPreviewFiles = ({
    hasProcessingStep = true,
}: {
    hasProcessingStep?: boolean
}) => {
    const {
        flightId,
        portfolioId,
        fireId,
        uploads,
        useProcessing,
        uploadFlight,
        minUploads = 3,
        usableUploads,
        addOnType,
        selectFlightById,
        selectIsUploading,
        selectFinishedUploadSize,
        selectUploadSize,
        selectUploadStateSummary,
        selectOverlapLayerIsEnabled,
        setOverlapLayer,
        uploadFlowWarnings,
    } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const { isRestricted } = useAddOnRestricted(addOnType)
    const { removeSelected, isRemoving } = useRemoveSelectedUploads()
    useImportArrivalWatcher()

    const [viewMode, setViewMode] = useState<"Grid" | "List">("Grid")
    const [isShowingWarningModal, setIsShowingWarningModal] = useState(false)
    const [isShowingProcessingOptionsModal, setIsShowingProcessingOptionsModal] = useState(false)
    const [isShowingRapidModeModal, setIsShowingRapidModeModal] = useState(false)
    const [uploadInitiated, setUploadInitiated] = useState(false)
    const pendingRapidModeAction = useRef<(() => void) | null>(null)

    const flight = useAppSelector(selectFlightById(flightId))
    const rawUploads = useAppSelector(selectRawOrthoUploadsForSet(flightId))
    const averageUploadSpeed = useUploadSpeedForFlightId(flightId)
    const finishedUploadSize = useAppSelector(selectFinishedUploadSize(flightId))
    const uploadSize = useAppSelector(selectUploadSize(flightId))
    const [processingMode, _setProcessingMode] = useState(
        processingModeCache[flightId] ?? OrthoProcessingMode.General
    )
    const setProcessingMode = useCallback(
        (mode: OrthoProcessingMode) => {
            processingModeCache[flightId] = mode
            _setProcessingMode(mode)
        },
        [flightId]
    )

    const withRapidModeCheck = useCallback(
        (action: () => void) => {
            if (processingMode !== OrthoProcessingMode.Rapid) {
                action()
                return
            }
            if (localStorage.getItem(RAPID_MODE_DONT_SHOW_KEY) === "true") {
                action()
                return
            }
            pendingRapidModeAction.current = action
            setIsShowingRapidModeModal(true)
        },
        [processingMode]
    )

    const lockInRapidModeConfig = useCallback(async () => {
        if (processingMode !== OrthoProcessingMode.Rapid) return
        await fetchApiGateway("put /org/:orgId/project/:projectId/flight/:flightId/config", {
            params: { orgId: portfolioId, projectId: fireId, flightId },
            body: {
                flightType: "orthomosaic",
                processingMode: "oneShotWithPreview",
                imageCount: uploads.length,
            },
        })
    }, [processingMode, portfolioId, fireId, flightId, uploads.length])

    const urbanModeDisabled = uploads.length > MAX_UPLOADS_FOR_URBAN_MODE

    useEffect(() => {
        if (urbanModeDisabled && processingMode === OrthoProcessingMode.Urban) {
            setProcessingMode(OrthoProcessingMode.General)
        }
    }, [urbanModeDisabled, processingMode, setProcessingMode])

    const { processCallback, dependencies } = useProcessing()
    const { createJob, createImportJob } = useAsyncCallback(
        {
            createJob: async () => {
                await processCallback(processingMode, uploads.length)
                dispatch(
                    deactivateFlightPath({
                        flightId,
                    })
                )
            },
            createImportJob: async () => {
                await processCallback(processingMode, uploads.length)
                dispatch(
                    deactivateFlightPath({
                        flightId,
                    })
                )
            },
        },
        [...dependencies, processingMode]
    )

    const summary = useAppSelector(selectUploadStateSummary(flightId))
    const isUploading = useAppSelector(selectIsUploading(flightId))
    const csvFlights = useAppSelector(selectCsvFlights())

    // .owi (RGB) files can't upload until MMA telemetry is attached and every one
    // has a matching row.
    const { csvPending, hasInvalidTelemetry } = useCsvTelemetryGating({
        uploads,
        flightId,
        csvFlights,
    })

    const canUpload = useMemo(
        () =>
            uploads.length >= minUploads &&
            !csvPending &&
            !hasInvalidTelemetry &&
            uploads.every(
                (upload) => "state" in upload && upload.state !== ImageProcessingState.Processing
            ),
        [uploads, minUploads, csvPending, hasInvalidTelemetry]
    )

    const canProcess = useMemo(
        () =>
            !!(
                canUpload &&
                uploads.every(
                    (upload) =>
                        "state" in upload && upload.state === ImageProcessingState.IngestDone
                )
            ),
        [uploads, canUpload]
    )

    const isImportOnly =
        (rawUploads?.every((u) => isUsable(u)?.isRemoteImport) ?? true) &&
        (flight?.images?.length ?? 0) >= minUploads

    const rapidModeImportCanProcess =
        processingMode === OrthoProcessingMode.Rapid &&
        !uploadInitiated &&
        !isUploading &&
        isImportOnly

    const stagingDone = useMemo(
        () =>
            !!usableUploads.length &&
            usableUploads.every((upload) => upload.state >= ImageProcessingState.ProcessingDone),
        [usableUploads]
    )

    useEffectUnsafe(() => {
        if (stagingDone) {
            dispatch(
                activateFlightPath({
                    flightId,
                    uploads: uploads as UploadState<{ id: string; error: Error }>[],
                    autoZoom: true,
                    useExistingPath: false,
                })
            )
        }
    }, [stagingDone, dispatch, flightId])

    let warningCount = 0
    if (!!uploadFlowWarnings?.[flightId])
        Object.values(uploadFlowWarnings[flightId]).forEach((warning) => {
            warningCount += warning.ids.length
        })

    const exceededUsage = flightExceededUsage[flightId]
    const hasExceededUploads = !isUploading && exceededUsage > 0
    const isPaywalled = hasExceededUploads || isRestricted

    const isReadOnly = isUploading || canProcess
    return (
        <>
            <PreviewFilesViewSwitcher viewMode={viewMode} setViewMode={setViewMode} />
            {/* Upload Info*/}
            {isUploading && (
                <div className="flex items-center justify-between">
                    {summary.stateCount[ImageProcessingState.IngestDone] > 0 ? (
                        <div className="flex items-center gap-1">
                            <h5 className="text-gray-0">Uploading</h5>
                            <h5 className="text-gray-100">
                                {`${summary.stateCount[ImageProcessingState.IngestDone] || 0} / ${summary.uploadCount}`}
                            </h5>
                        </div>
                    ) : (
                        <h5 className="text-gray-0">Upload Queued</h5>
                    )}
                    {summary.stateCount[ImageProcessingState.IngestDone] > 0 && (
                        <h5 className="text-gray-100">{averageUploadSpeed} Mbps</h5>
                    )}
                </div>
            )}
            {canProcess && (
                <div className="flex items-center gap-1">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Upload Complete</h5>
                </div>
            )}
            {hasExceededUploads && <GenericDataUsage remainingBytes={exceededUsage} />}
            {!isUploading && !canProcess && !isPaywalled && uploads.length >= minUploads && (
                <PreviewFilesWarningsAndErrors />
            )}
            {uploads.length < minUploads && (
                <h5 className="text-red-500 mt-2">
                    {`Add ${minUploads - uploads.length} more file${
                        minUploads - uploads.length === 1 ? "" : "s"
                    } to continue`}
                </h5>
            )}
            <div className="mt-2 flex flex-col min-h-0">
                {/* Grid View, List View, and Upload metrics */}
                <PreviewFilesUploads
                    viewMode={viewMode}
                    isPaywalled={isPaywalled}
                    removeSelected={removeSelected}
                    isRemoving={isRemoving}
                />
                {csvPending && (
                    <small className="text-yellow-400 mt-1 block">
                        These files require a telemetry CSV - drop it using &quot;Add more
                        files&quot;
                    </small>
                )}
                {hasInvalidTelemetry && (
                    <small className="text-red-400 mt-1 block">
                        No telemetry entry found for one or more files. Remove them and re-upload
                        with a matching CSV.
                    </small>
                )}
                {!isUploading && setOverlapLayer && selectOverlapLayerIsEnabled && (
                    <OverlapLayerToggle
                        selectOverlapEnabled={selectOverlapLayerIsEnabled}
                        setOverlapLayer={setOverlapLayer}
                    />
                )}
                <InspectorPanelMetadata
                    entity={flight as SidePanelLayer}
                    uploads={uploads as GenericUpload[]}
                    uploadSize={uploadSize}
                    completedUploadCount={summary.stateCount[ImageProcessingState.IngestDone] || 0}
                    hideCreator
                    hideFileCount={finishedUploadSize > 0}
                    className=""
                />
                {!(processingMode === OrthoProcessingMode.Rapid
                    ? (isUploading || canProcess) && !isImportOnly
                    : isUploading && flight.processAfterUpload) && (
                    <>
                        <div className="border-b border-gray-700" />
                        <div className="flex flex-col text-gray-0 py-2 gap-1">
                            <div className="flex h-[18px]">
                                <small className="text-gray-300">Processing Options</small>
                                <div className="ml-auto relative">
                                    <div
                                        className="flex gap-1 group cursor-pointer"
                                        onClick={() => setIsShowingProcessingOptionsModal(true)}
                                    >
                                        <small className="text-[12px] leading-[18px] font-normal underline text-gray-500 group-hover:text-blue-500">
                                            Learn More
                                        </small>
                                        <Icons.SmallIcon
                                            Icon={Icons.Information}
                                            className="text-gray-500 group-hover:text-gray-0"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div
                                className="flex items-center gap-2 py-1 rounded-md group hover:bg-gray-700 cursor-pointer"
                                onClick={() => setProcessingMode(OrthoProcessingMode.General)}
                            >
                                <RadioButton
                                    isSelected={processingMode === OrthoProcessingMode.General}
                                    onClick={() => setProcessingMode(OrthoProcessingMode.General)}
                                    size="small"
                                />
                                <small
                                    className={classNames("group-hover:text-gray-0", {
                                        "text-gray-100":
                                            processingMode !== OrthoProcessingMode.General,
                                    })}
                                >
                                    General Mode
                                </small>
                            </div>
                            <div
                                className={classNames(
                                    "flex items-center gap-2 py-1 rounded-md group",
                                    {
                                        "hover:bg-gray-700": !urbanModeDisabled,
                                        "cursor-pointer": !urbanModeDisabled,
                                    }
                                )}
                                onClick={() => {
                                    if (!urbanModeDisabled)
                                        setProcessingMode(OrthoProcessingMode.Urban)
                                }}
                            >
                                <RadioButton
                                    isSelected={processingMode === OrthoProcessingMode.Urban}
                                    onClick={() => {
                                        if (!urbanModeDisabled)
                                            setProcessingMode(OrthoProcessingMode.Urban)
                                    }}
                                    size="small"
                                    disabled={urbanModeDisabled}
                                />
                                <small
                                    className={classNames({
                                        "text-gray-500": urbanModeDisabled,
                                        "text-gray-100":
                                            processingMode !== OrthoProcessingMode.Urban &&
                                            !urbanModeDisabled,
                                        "group-hover:text-gray-0": !urbanModeDisabled,
                                    })}
                                >
                                    Urban Mode
                                </small>
                                {urbanModeDisabled && (
                                    <div className="text-red-500 relative group/tooltip">
                                        <small>Too many images</small>
                                        <Tooltip
                                            placement="top"
                                            title={`When using Urban mode, for best results use less than ${MAX_UPLOADS_FOR_URBAN_MODE} images.`}
                                            textClass="w-[160px] whitespace-normal break-words text-center text-gray-100"
                                        />
                                    </div>
                                )}
                            </div>
                            {!isUploading && (!canProcess || isImportOnly) && (
                                <div
                                    className="flex items-center gap-2 py-1 rounded-md group hover:bg-gray-700 cursor-pointer"
                                    onClick={() => setProcessingMode(OrthoProcessingMode.Rapid)}
                                >
                                    <RadioButton
                                        isSelected={processingMode === OrthoProcessingMode.Rapid}
                                        onClick={() => setProcessingMode(OrthoProcessingMode.Rapid)}
                                        size="small"
                                    />
                                    <small
                                        className={classNames("group-hover:text-gray-0", {
                                            "text-gray-100":
                                                processingMode !== OrthoProcessingMode.Rapid,
                                        })}
                                    >
                                        Rapid Mode
                                    </small>
                                </div>
                            )}
                        </div>
                    </>
                )}
                <div className="border-b border-gray-700" />
                {!isReadOnly && createJob && hasProcessingStep && (
                    <>
                        <AutoProcessToggle
                            process={createJob}
                            forceEnable={processingMode === OrthoProcessingMode.Rapid}
                        />
                        <div className="border-b border-gray-700" />
                    </>
                )}
                {/* action buttons */}
                <div className="flex gap-2 items-center mt-2">
                    <DeleteFlight />
                    {!isUploading && !canProcess && (
                        <ButtonPrimary
                            title={isUploading ? "Uploading..." : "Upload"}
                            className="w-full !px-0"
                            onClick={() => {
                                const doUpload = async () => {
                                    if (warningCount > 0) {
                                        setIsShowingWarningModal(true)
                                        return
                                    }
                                    await lockInRapidModeConfig()
                                    setUploadInitiated(true)
                                    dispatch(uploadFlight(flightId))
                                    dispatch(
                                        addUploadTimestamp({
                                            flightId,
                                            timestamp: new Date().toISOString(),
                                        })
                                    )
                                }
                                withRapidModeCheck(doUpload)
                            }}
                            disabled={!canUpload || isUploading || !isEditor || hasExceededUploads}
                        />
                    )}
                    {hasProcessingStep &&
                        (processingMode === OrthoProcessingMode.Rapid
                            ? rapidModeImportCanProcess && (
                                  <ButtonPrimary
                                      title="Process"
                                      className="w-full !px-0"
                                      onClick={createImportJob}
                                      disabled={!isEditor || hasExceededUploads}
                                  />
                              )
                            : canProcess && (
                                  <ButtonPrimary
                                      title="Process"
                                      className="w-full !px-0"
                                      onClick={createJob}
                                      disabled={
                                          !canUpload ||
                                          isUploading ||
                                          !isEditor ||
                                          hasExceededUploads
                                      }
                                  />
                              ))}
                </div>
            </div>
            {isShowingWarningModal && (
                <UploadWarningModal
                    onHideModal={() => setIsShowingWarningModal(false)}
                    removeSelected={removeSelected}
                    isRemoving={isRemoving}
                    beforeUpload={lockInRapidModeConfig}
                />
            )}
            {isShowingProcessingOptionsModal && (
                <ProcessingOptionsModal
                    enabledModes={["general", "urban", "rapid"]}
                    closeModal={() => setIsShowingProcessingOptionsModal(false)}
                />
            )}
            {isShowingRapidModeModal && (
                <RapidModeModal
                    cancel={() => setIsShowingRapidModeModal(false)}
                    proceed={() => {
                        setIsShowingRapidModeModal(false)
                        pendingRapidModeAction.current?.()
                    }}
                />
            )}
        </>
    )
}

const OverlapLayerToggle = ({
    selectOverlapEnabled,
    setOverlapLayer,
}: {
    setOverlapLayer: ({ id, active }: { id: ID; active: boolean }) => AnyAction
    selectOverlapEnabled: (flightId: string) => (state: AnyAction) => boolean
}) => {
    const { flightId } = useUploadFlowContext()
    const dispatch = useAppDispatch()
    const orthoOverlapIsEnabled = useAppSelector(selectOverlapEnabled(flightId))
    return (
        <InspectorPanelKeyValueToggle
            isToggled={!!orthoOverlapIsEnabled}
            setIsToggled={() =>
                dispatch(
                    setOverlapLayer!({
                        id: flightId,
                        active: !orthoOverlapIsEnabled,
                    })
                )
            }
            keyTitle="Show Overlap"
            className="my-3"
            toggleClassName="justify-between"
        />
    )
}
